import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ArrowLeft, Gauge, ChevronRight, Ban, AlertTriangle,
  CheckCircle2, XCircle, MinusCircle, Wrench, FileText, ShieldCheck, Lock, Loader2, TrendingUp,
  Clock, Filter, FileDown, PlayCircle, Plus, Undo2,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import useEhCompacto from '../../../utils/useEhCompacto';
import { aguardandoDe, diasParado, emAndamento, ordenarPorParado } from './conservacao-fila-utils';
import ConservacaoOrdens from './ConservacaoOrdens';
import ConservacaoDashboard from './ConservacaoDashboard';
import ImagemSharePoint from './ImagemSharePoint';
import { perfilNoModulo } from '../../../utils/perfisDerivados';
import { classificarIcp } from './conservacao-icp-regras';

const user = JSON.parse(localStorage.getItem('user') || '{}');
const PERFIL = perfilNoModulo(user?.perfil, 'manutencao') || '';
// Quem pode assinar cada papel (espelha PERFIL_POR_PAPEL do backend — a decisão
// real é do servidor; aqui é só para mostrar/esconder o botão certo).
const podeGerente = ['Gerente', 'Admin'].includes(PERFIL);
const podeConsultor = ['Manutencao', 'Admin', 'Diretoria'].includes(PERFIL);

// Situações do programa (ordem canônica) + rótulo curto para os chips da linha da loja.
const STATUS_OPCOES = ['Rascunho', 'Inspecionado', 'Em execução', 'Certificado', 'Cancelado'];
const STATUS_CURTO = {
  'Rascunho': 'Rascunho', 'Inspecionado': 'Inspecionado', 'Em execução': 'Execução',
  'Certificado': 'Certificado', 'Cancelado': 'Cancelado',
};

// Célula base da tabela dinâmica (cabeçalho e corpo) — mesmo padrão da Certificação.
const thBase = { padding: '12px 18px', borderBottom: '1px solid #e2e8f0', fontSize: 12.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', whiteSpace: 'nowrap' };
const tdBase = { padding: '11px 18px', fontSize: 14, color: '#0f172a', verticalAlign: 'middle' };

// Cor da coluna "Aguardando": distingue quem está devendo. Validação pendente é
// âmbar (alguém precisa assinar); trabalho em curso é índigo — a cor do módulo,
// porque é a própria Manutenção (ou a inspeção) que está com ele.
const AGUARDANDO_ESTILO = {
  validacao: { bg: '#fef3c7', color: '#92400e' },
  trabalho: { bg: '#eef2ff', color: '#4338ca' },
  nenhum: { bg: 'transparent', color: '#cbd5e1' },
};

// Cor do tempo parado — mesmos cortes da Certificação (acima de 30 dias é caso
// perdido de vista; acima de 14 já pede ação). Só colore a fila: programa
// certificado não está "parado", está pronto.
const corParado = (dias) => (dias > 30 ? '#b91c1c' : dias > 14 ? '#b45309' : '#64748b');

// Abas: a fila (o que exige acao) separada do arquivo (o que ja terminou).
// ⚠️ 'Cancelado' não entra em nenhuma das duas — não é fila nem conquista; ele
// aparece em "Todos", que é o escape e fecha a contagem com o total.
const ABAS = [
  { chave: 'andamento', label: 'Em andamento', filtro: emAndamento },
  { chave: 'certificados', label: 'Certificados', filtro: (p) => p.STATUS === 'Certificado' },
  { chave: 'todos', label: 'Todos', filtro: () => true },
];

// =====================================================================
// Consulta dos Programas de Conservação Predial gravados.
//
// Recortada por loja NO BACKEND (gerente vê só a própria unidade) — a tela
// não repete essa regra, só a consome.
//
// ⚠️ Esta tela se chama "programas gravados", mas o que ela contém é FILA DE
// TRABALHO — como a irmã da Certificação de Equipamentos. Por isso abre em
// "Em andamento", ordenada pelo mais parado, e traz a coluna "Aguardando":
// quem está segurando cada programa. A leitura de fila mora em
// `conservacao-fila-utils.js`, pura e testada; aqui fica só a apresentação.
//
// ⚠️ A dívida da Conservação tem um degrau que a Certificação não tem: as
// Ordens de Serviço. Com OS em aberto o programa está com quem executa o
// serviço, não com quem valida — o backend recusa a assinatura (409).
//
// Até 2026-08-18 a lista era uma ÁRVORE Loja → Ano → Programa, fechada por
// padrão: eram 2 cliques até enxergar qualquer linha, num módulo que tinha 2
// programas no total (medido em 18/08) — a árvore escondia 100% do conteúdo.
// O resumo por loja que ela trazia na linha-mãe (chips de situação + último
// ICP certificado) não se perdeu: é o "Histórico e indicadores", com ranking.
//
// ⚠️ A lista mostra a DATA DA INSPEÇÃO, não só o ano, de propósito: a
// re-inspeção no mesmo ano é permitida, então "2026" sozinho não distingue
// dois programas da mesma loja.
// =====================================================================

const API = '/api/manutencao/conservacao';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

// ⚠️ A COR SAI DA CLASSIFICAÇÃO, NUNCA DE CORTES PRÓPRIOS. Este mapa já foi
// uma segunda cópia dos cortes do ICP (95/90/80/70) e, quando a régua virou
// binária em 2026-09-09 e os cortes caíram para 90/80/65/50, ele passaria a
// pintar um ICP de 85 com a cor de "Bom" enquanto o rótulo ao lado dizia
// "Muito Bom" — divergência que ninguém liga à causa. Os cortes vivem em UM
// lugar: conservacao-icp-regras.js (espelho travado do motor do servidor).
const CORES_ICP = {
  Excelente: { bg: '#d1fae5', color: '#065f46' },
  'Muito Bom': { bg: '#dbeafe', color: '#1e40af' },
  Bom: { bg: '#e0e7ff', color: '#4338ca' },
  Regular: { bg: '#fef3c7', color: '#92400e' },
  'Necessita Intervenção': { bg: '#fee2e2', color: '#991b1b' },
};

const corIcp = (icp) => {
  if (icp == null) return { bg: '#f1f5f9', color: '#64748b' };
  return CORES_ICP[classificarIcp(icp)] || { bg: '#f1f5f9', color: '#64748b' };
};

const corStatus = (s) => {
  if (s === 'Certificado') return { bg: '#d1fae5', color: '#065f46' };
  if (s === 'Inspecionado') return { bg: '#dbeafe', color: '#1e40af' };
  if (s === 'Em execução') return { bg: '#fef3c7', color: '#92400e' };
  if (s === 'Cancelado') return { bg: '#fee2e2', color: '#991b1b' };
  return { bg: '#f1f5f9', color: '#475569' }; // Rascunho
};

const iconeStatusItem = (s) => {
  if (s === 'Conforme') return <CheckCircle2 size={15} color="#059669" />;
  if (s === 'Desgaste') return <AlertTriangle size={15} color="#b45309" />;
  if (s === 'Necessita Intervenção') return <XCircle size={15} color="#b91c1c" />;
  return <MinusCircle size={15} color="#94a3b8" />;
};

const dataBr = (d) => (d ? new Date(d).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '—');

// Deixa claro em que ponto da esteira o programa está — vai no `title` da linha,
// onde cabe a frase inteira que o chip curto não comporta.
const descricaoSituacao = (s) => ({
  'Rascunho': 'Rascunho — inspeção em andamento, ainda não finalizada',
  'Inspecionado': 'Inspeção finalizada — as Ordens de Serviço nasceram dela',
  'Em execução': 'Ordens de Serviço em andamento',
  'Certificado': 'Certificado — validado por gerente e consultor, sem OS em aberto',
  'Cancelado': 'Cancelado',
}[s] || '');

// `acoesExtras`: botões de ação injetados pelo pai (Nova Inspeção / Gerenciar
// versões) — renderizados no cabeçalho quando esta é a tela inicial do sub-hub.
// `onContinuarRascunho(programa)`: retoma a inspeção de um Rascunho.
const ConservacaoHistorico = ({ acoesExtras, onContinuarRascunho, podeGerenciar }) => {
  const compacto = useEhCompacto();
  const [programas, setProgramas] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [detalhe, setDetalhe] = useState(null);
  const [verDashboard, setVerDashboard] = useState(false);

  // Filtros (client-side — a lista é pequena e o filtro é instantâneo).
  const [aba, setAba] = useState('andamento'); // abre na FILA, não no arquivo
  const [fLoja, setFLoja] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [fPeriodo, setFPeriodo] = useState(''); // mês de referência 'AAAA-MM' (input type=month)

  const carregar = useCallback(async () => {
    setCarregando(true); setErro('');
    try {
      const r = await fetch(API, { headers: auth() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setProgramas(await r.json());
    } catch (e) {
      setErro('Erro ao carregar os programas.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const lojas = useMemo(() => [...new Set(programas.map((p) => p.LOJA).filter(Boolean))].sort(), [programas]);
  const dataRef = (p) => String(p.DATA_INSPECAO || p.DATA_CRIACAO || '').slice(0, 10);

  // Base: tudo o que passa nos filtros de recorte (loja/período).
  // A situação fica FORA daqui de propósito — é ela que alimenta as contagens
  // das abas, que precisam continuar corretas mesmo com uma situação escolhida.
  const baseFiltrada = useMemo(() => programas.filter((p) => {
    if (fLoja && p.LOJA !== fLoja) return false;
    const d = dataRef(p);
    if (fPeriodo && (!d || d.slice(0, 7) !== fPeriodo)) return false; // período = mês de referência
    return true;
  }), [programas, fLoja, fPeriodo]);

  const contagemAba = useMemo(() => Object.fromEntries(
    ABAS.map((a) => [a.chave, baseFiltrada.filter(a.filtro).length])
  ), [baseFiltrada]);

  // Só as situações que existem na aba corrente vão para o select — oferecer
  // "Certificado" dentro da fila renderia lista vazia sem explicar por quê.
  const statusDaAba = useMemo(() => {
    const daAba = baseFiltrada.filter(ABAS.find((a) => a.chave === aba).filtro);
    return STATUS_OPCOES.filter((s) => daAba.some((p) => p.STATUS === s));
  }, [baseFiltrada, aba]);

  // Troca de aba: derruba a situação escolhida se ela não existir na aba nova
  // (senão a tela abriria vazia com um filtro invisível ligado).
  const trocarAba = (chave) => {
    setAba(chave);
    setFStatus((s) => (s && !baseFiltrada.some((p) => ABAS.find((a) => a.chave === chave).filtro(p) && p.STATUS === s) ? '' : s));
  };

  // A lista que vai à tela. Na FILA, o mais parado no topo — é a ordem que a
  // torna útil. No arquivo, o mais recente primeiro: lá "parado há" não quer
  // dizer nada, e o que se procura é a última certificação.
  const listaFiltrada = useMemo(() => {
    const visivel = baseFiltrada
      .filter(ABAS.find((a) => a.chave === aba).filtro)
      .filter((p) => !fStatus || p.STATUS === fStatus);
    return aba === 'certificados'
      ? visivel.slice().sort((a, b) => dataRef(b).localeCompare(dataRef(a)))
      : ordenarPorParado(visivel);
  }, [baseFiltrada, aba, fStatus]);

  const temFiltro = fLoja || fStatus || fPeriodo;
  const limparFiltros = () => { setFLoja(''); setFStatus(''); setFPeriodo(''); };

  const exportarExcel = () => {
    const linhas = listaFiltrada.map((p) => ({
      'Programa': p.ID, 'Loja': p.LOJA, 'Ano': p.ANO, 'Data Inspeção': dataRef(p),
      'Situação': p.STATUS,
      // As duas colunas da leitura de fila — é por elas que se cobra.
      'Aguardando': aguardandoDe(p).texto,
      'Parado há (dias)': emAndamento(p) ? diasParado(p) : '',
      'ICP (%)': p.ICP, 'Classificação': p.CLASSIFICACAO_ICP || '',
      'Gerente': p.GERENTE || '', 'Dias': p.DIAS_TOTAIS ?? '',
      'Itens avaliados': p.QTD_RESPOSTAS, 'Não conformidades': Number(p.QTD_NAO_CONFORMIDADES) || 0,
      'OS em aberto': Number(p.QTD_OS_ABERTAS) || 0,
      'Checklist': p.NOME_VERSAO,
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(linhas), 'Conservação');
    XLSX.writeFile(wb, `Conservacao_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  if (detalhe) {
    return <ProgramaDetalhe id={detalhe} onVoltar={() => { setDetalhe(null); carregar(); }} podeGerenciar={podeGerenciar} />;
  }
  if (verDashboard) {
    return <ConservacaoDashboard onVoltar={() => setVerDashboard(false)} />;
  }

  // Abrir a linha: rascunho volta para a inspeção (é o que se quer fazer com
  // ele); o resto abre o detalhe.
  const abrir = (p) => ((p.STATUS === 'Rascunho' && onContinuarRascunho) ? onContinuarRascunho(p) : setDetalhe(p.ID));

  // No compacto (celular) cada filtro ocupa a linha inteira — senão os selects
  // ficam espremidos e cortados.
  const selStyle = {
    padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, color: '#475569', background: '#fff',
    ...(compacto ? { width: '100%', boxSizing: 'border-box' } : {}),
  };
  const filtroItem = compacto ? { flex: '1 1 100%' } : {};

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, flex: 1, minWidth: 160, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>Programas gravados</h3>
        {acoesExtras}
        <button onClick={() => setVerDashboard(true)} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
          background: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe',
          borderRadius: 8, fontWeight: 700, cursor: 'pointer',
        }}>
          <TrendingUp size={15} /> Histórico e indicadores
        </button>
        {listaFiltrada.length > 0 && (
          <button onClick={exportarExcel} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#fff', color: '#4338ca', border: '1px solid #c7d2fe', borderRadius: 8, fontWeight: 800, cursor: 'pointer' }}>
            <FileDown size={16} /> Exportar Excel
          </button>
        )}
      </div>

      {/* Abas: a fila primeiro, o arquivo depois. */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 12, borderBottom: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
        {ABAS.map((a) => {
          const ativa = aba === a.chave;
          return (
            <button key={a.chave} onClick={() => trocarAba(a.chave)} style={{
              padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer',
              fontWeight: ativa ? 800 : 600, fontSize: 14, color: ativa ? '#4f46e5' : '#64748b',
              borderBottom: `2px solid ${ativa ? '#4f46e5' : 'transparent'}`, marginBottom: -1,
            }}>
              {a.label} <span style={{ fontVariantNumeric: 'tabular-nums', color: ativa ? '#4f46e5' : '#94a3b8' }}>({contagemAba[a.chave]})</span>
            </button>
          );
        })}
      </div>

      {/* Barra de filtros */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 12, marginBottom: 16 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#475569', fontWeight: 700, fontSize: 13 }}><Filter size={15} /> Filtros</span>
        <select value={fLoja} onChange={(e) => setFLoja(e.target.value)} style={selStyle}>
          <option value="">Todas as lojas</option>
          {lojas.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} style={selStyle}>
          <option value="">Todas as situações</option>
          {statusDaAba.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <label style={{ fontSize: 13, color: '#64748b', display: 'inline-flex', alignItems: 'center', gap: 6, ...filtroItem }}>Período <input type="month" value={fPeriodo} onChange={(e) => setFPeriodo(e.target.value)} style={selStyle} /></label>
        {temFiltro && (
          <button onClick={limparFiltros} style={{ padding: '8px 12px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>Limpar</button>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 13, color: '#94a3b8' }}>{listaFiltrada.length} de {programas.length}</span>
      </div>

      {erro && <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginBottom: 12 }}>{erro}</div>}

      {carregando ? <p style={{ color: '#64748b' }}>Carregando…</p> : programas.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', background: '#f8fafc', borderRadius: 12, color: '#64748b' }}>
          Nenhum programa registrado ainda.
        </div>
      ) : listaFiltrada.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', background: '#f8fafc', borderRadius: 12, color: '#64748b' }}>
          {/* Sem filtro ligado, a aba vazia é um fato do módulo — mandar
              procurar "os filtros escolhidos" faria caçar o que não existe. */}
          {temFiltro ? 'Nenhum programa para os filtros escolhidos.'
            : aba === 'andamento' ? 'Nenhum programa em andamento — a fila está limpa.'
              : aba === 'certificados' ? 'Nenhum programa certificado ainda.'
                : 'Nenhum programa para os filtros escolhidos.'}
        </div>
      ) : compacto ? (
        // ── Celular: um cartão por programa. Oito colunas não cabem numa tela
        // de mão, e é no celular que o gerente valida — a pendência tem de
        // estar legível sem rolagem lateral.
        <div style={{ display: 'grid', gap: 10 }}>
          {listaFiltrada.map((p) => {
            const cs = corStatus(p.STATUS);
            const ci = corIcp(p.ICP);
            const ag = aguardandoDe(p);
            const agEstilo = AGUARDANDO_ESTILO[ag.tipo];
            const dias = emAndamento(p) ? diasParado(p) : null;
            const ehRascunho = p.STATUS === 'Rascunho';
            return (
              <div key={p.ID} onClick={() => abrir(p)} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 14, cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
                  <span style={{ fontWeight: 800, fontSize: 15, color: '#0f172a' }}>{p.LOJA}</span>
                  <span style={{ fontWeight: 700, fontSize: 14, color: '#334155' }}>Conservação {p.ANO}</span>
                  <span style={{ marginLeft: 'auto', fontSize: 12.5, color: '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>#{p.ID}</span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                  <span style={{ padding: '3px 9px', borderRadius: 999, fontSize: 11, fontWeight: 800, background: cs.bg, color: cs.color }}>{STATUS_CURTO[p.STATUS] || p.STATUS}</span>
                  {ag.tipo !== 'nenhum' && (
                    <span style={{ padding: '3px 9px', borderRadius: 999, fontSize: 11, fontWeight: 800, background: agEstilo.bg, color: agEstilo.color }}>{ag.texto}</span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 12.5, color: '#64748b' }}>
                  {dias !== null && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: corParado(dias), fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                      <Clock size={13} /> parado há {dias} {dias === 1 ? 'dia' : 'dias'}
                    </span>
                  )}
                  {p.ICP != null && (
                    <span style={{ fontWeight: 800, color: ci.color, fontVariantNumeric: 'tabular-nums' }}>{Number(p.ICP).toFixed(1)}%</span>
                  )}
                  <span style={{ marginLeft: 'auto', fontVariantNumeric: 'tabular-nums' }}>{dataBr(p.DATA_INSPECAO)}</span>
                </div>
                {ehRascunho && onContinuarRascunho && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: '#7c3aed', color: '#fff', borderRadius: 8, fontWeight: 800, fontSize: 12.5 }}>
                      <PlayCircle size={13} /> Continuar
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
              <thead style={{ background: '#f8fafc' }}>
                <tr>
                  <th style={{ ...thBase, textAlign: 'left' }}>Loja</th>
                  <th style={{ ...thBase, textAlign: 'left' }}>Programa</th>
                  <th style={{ ...thBase, textAlign: 'left' }}>Nº</th>
                  <th style={{ ...thBase, textAlign: 'left' }}>Situação</th>
                  <th style={{ ...thBase, textAlign: 'left' }}>Aguardando</th>
                  {/* Só na fila: programa certificado não está parado, está pronto. */}
                  {aba !== 'certificados' && <th style={{ ...thBase, textAlign: 'right' }}>Parado há</th>}
                  <th style={{ ...thBase, textAlign: 'right' }}>ICP</th>
                  <th style={{ ...thBase, textAlign: 'left' }}>Inspeção</th>
                  <th style={{ ...thBase, textAlign: 'right' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {listaFiltrada.map((p) => {
                  const cs = corStatus(p.STATUS);
                  const ci = corIcp(p.ICP);
                  const ag = aguardandoDe(p);
                  const agEstilo = AGUARDANDO_ESTILO[ag.tipo];
                  const dias = emAndamento(p) ? diasParado(p) : null;
                  const ehRascunho = p.STATUS === 'Rascunho';
                  const nc = Number(p.QTD_NAO_CONFORMIDADES) || 0;
                  return (
                    <tr key={p.ID} onClick={() => abrir(p)} title={descricaoSituacao(p.STATUS)}
                      style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer', background: '#fff' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#faf5ff'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}>
                      <td style={{ ...tdBase, fontWeight: 800 }}>{p.LOJA}</td>
                      <td style={tdBase} title={p.NOME_VERSAO}>
                        <div style={{ fontWeight: 700, color: '#334155', fontSize: 13.5 }}>Conservação {p.ANO}</div>
                        {/* O nome do checklist fica no title: escrito aqui, ele
                            repete o "Conservação AAAA" acima e quebra a célula
                            em três linhas. Itens e NC dizem mais em menos. */}
                        <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 2 }}>
                          {p.QTD_RESPOSTAS} itens · {nc} NC
                        </div>
                      </td>
                      <td style={{ ...tdBase, color: '#94a3b8', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>#{p.ID}</td>
                      <td style={tdBase}>
                        <span style={{ padding: '3px 9px', borderRadius: 999, fontSize: 11, fontWeight: 800, background: cs.bg, color: cs.color, whiteSpace: 'nowrap' }}>
                          {STATUS_CURTO[p.STATUS] || p.STATUS}
                        </span>
                      </td>
                      <td style={tdBase}>
                        {ag.tipo === 'nenhum' ? <span style={{ color: '#cbd5e1' }}>—</span> : (
                          <span style={{ padding: '3px 9px', borderRadius: 999, fontSize: 11.5, fontWeight: 800, background: agEstilo.bg, color: agEstilo.color, whiteSpace: 'nowrap' }}>
                            {ag.texto}
                          </span>
                        )}
                      </td>
                      {aba !== 'certificados' && (
                        <td style={{ ...tdBase, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          {dias === null ? <span style={{ color: '#cbd5e1' }}>—</span> : (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end', color: corParado(dias), fontWeight: dias > 14 ? 800 : 600 }}>
                              {dias > 14 && <Clock size={13} />}{dias} {dias === 1 ? 'dia' : 'dias'}
                            </span>
                          )}
                        </td>
                      )}
                      <td style={{ ...tdBase, textAlign: 'right' }}>
                        {/* ICP não aparece em rascunho — a inspeção ainda não fechou. */}
                        {p.ICP == null ? <span style={{ color: '#cbd5e1' }}>—</span> : (
                          <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                            <span style={{ fontSize: 15, fontWeight: 800, color: ci.color, fontVariantNumeric: 'tabular-nums' }}>{Number(p.ICP).toFixed(1)}%</span>
                            {p.CLASSIFICACAO_ICP && <span style={{ fontSize: 11, color: '#94a3b8' }}>{p.CLASSIFICACAO_ICP}</span>}
                          </span>
                        )}
                      </td>
                      <td style={{ ...tdBase, fontSize: 13, color: '#475569', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                        {dataBr(p.DATA_INSPECAO)}
                      </td>
                      <td style={{ ...tdBase, textAlign: 'right' }}>
                        {ehRascunho && onContinuarRascunho ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#7c3aed', fontWeight: 800, fontSize: 12.5 }}>
                            <PlayCircle size={15} /> Continuar
                          </span>
                        ) : (
                          <ChevronRight size={18} color="#cbd5e1" />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------
// Detalhe de UM programa
// ---------------------------------------------------------------------
const ProgramaDetalhe = ({ id, onVoltar, podeGerenciar }) => {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [verOrdens, setVerOrdens] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await fetch(`${API}/${id}`, { headers: auth() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setDados(await r.json());
    } catch (e) {
      setErro('Erro ao carregar o programa.');
    } finally {
      setCarregando(false);
    }
  }, [id]);

  useEffect(() => { carregar(); }, [carregar]);

  const cancelar = async () => {
    if (!window.confirm('Cancelar este programa? Ele deixa de valer, mas continua no histórico.')) return;
    const r = await fetch(`${API}/${id}/cancelar`, { method: 'POST', headers: auth() });
    const data = await r.json();
    if (!r.ok) { setErro(data?.message || 'Erro ao cancelar.'); return; }
    carregar();
  };

  // Download do PDF (Relatório Técnico + Certificado): a rota exige token,
  // então busca via fetch e dispara o blob (não dá para usar link simples).
  const baixarPdf = async () => {
    try {
      const resp = await fetch(`${API}/${id}/pdf`, { headers: auth() });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const u = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = u; a.download = `Conservacao_${dados?.programa?.LOJA || id}.pdf`; a.click();
      URL.revokeObjectURL(u);
    } catch (e) { setErro('Erro ao gerar o PDF.'); }
  };

  // Ordens de Serviço (Fase 2) — sai do detalhe e volta recarregando, porque
  // mexer nas ordens muda o STATUS do programa (a 1ª iniciada leva o programa
  // para "Em execução").
  if (verOrdens) {
    return <ConservacaoOrdens idPrograma={id} podeEditar={podeGerenciar}
      onVoltar={() => { setVerOrdens(false); carregar(); }} />;
  }

  if (carregando) return <p style={{ color: '#64748b' }}>Carregando…</p>;
  if (!dados) {
    return (
      <div>
        <button onClick={onVoltar} style={botaoVoltar}><ArrowLeft size={16} /> Voltar</button>
        <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginTop: 12 }}>{erro}</div>
      </div>
    );
  }

  const p = dados.programa;
  const ci = corIcp(p.ICP);
  // Agrupa os itens por área para a leitura — a query já vem ordenada pelo
  // roteiro da inspeção, então basta preservar a ordem de chegada.
  const porArea = dados.itens.reduce((acc, i) => {
    (acc[i.AREA_SNAPSHOT] = acc[i.AREA_SNAPSHOT] || []).push(i);
    return acc;
  }, {});

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <button onClick={onVoltar} style={botaoVoltar}><ArrowLeft size={16} /> Voltar</button>
        <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>
          {p.LOJA} · {dataBr(p.DATA_INSPECAO)}
        </h3>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {/* O PDF só faz sentido depois de finalizada a inspeção (antes é rascunho). */}
          {p.STATUS !== 'Rascunho' && (
            <button onClick={baixarPdf} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
              background: '#fff', color: '#4338ca', border: '1px solid #c7d2fe',
              borderRadius: 8, fontWeight: 700, cursor: 'pointer',
            }}>
              <FileText size={15} /> {p.STATUS === 'Certificado' ? 'Certificado (PDF)' : 'Relatório (PDF)'}
            </button>
          )}
          {/* As OS só existem depois que a inspeção foi finalizada. */}
          {p.STATUS !== 'Rascunho' && (
            <button onClick={() => setVerOrdens(true)} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
              background: '#4f46e5', color: '#fff', border: 'none',
              borderRadius: 8, fontWeight: 700, cursor: 'pointer',
            }}>
              <Wrench size={15} /> Ordens de Serviço
            </button>
          )}
          {podeGerenciar && ['Rascunho', 'Inspecionado'].includes(p.STATUS) && (
            <button onClick={cancelar} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 14px', background: '#fef2f2', color: '#b91c1c',
              border: '1px solid #fecaca', borderRadius: 8, fontWeight: 700, cursor: 'pointer',
            }}>
              <Ban size={15} /> Cancelar programa
            </button>
          )}
        </div>
      </div>

      {erro && <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginBottom: 16 }}>{erro}</div>}

      {/* --- validação em duas assinaturas (Fase 3) --- */}
      {p.STATUS !== 'Rascunho' && dados.validacao && (
        <PainelValidacao id={id} p={p} validacao={dados.validacao} onValidou={carregar} onErro={setErro} />
      )}

      {/* --- resumo --- */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
        <div style={{ padding: 16, background: ci.bg, borderRadius: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: ci.color, textTransform: 'uppercase' }}>ICP</div>
          <div style={{ fontSize: 30, fontWeight: 800, color: ci.color, fontVariantNumeric: 'tabular-nums' }}>
            {p.ICP == null ? '—' : `${Number(p.ICP).toFixed(2)}%`}
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, color: ci.color }}>{p.CLASSIFICACAO_ICP || '—'}</div>
        </div>
        <Card titulo="Status" valor={p.STATUS} />
        <Card titulo="Não conformidades" valor={dados.naoConformidades.total}
          rodape={dados.naoConformidades.livres > 0 ? `${dados.naoConformidades.livres} fora do checklist` : null} />
        <Card titulo="Dias totais" valor={p.DIAS_TOTAIS ?? '—'} />
        {/* ⚠️ O cartão "Especialista" saiu em 2026-08-18 junto com o campo:
            era texto livre digitado diferente a cada inspeção. Quem responde
            pela inspeção é o consultor que assina a certificação, logo abaixo. */}
        <Card titulo="Gerente" valor={p.GERENTE || '—'} />
      </div>

      {/* --- ICP por área --- */}
      {dados.icpPorArea.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 18, marginBottom: 20 }}>
          <h4 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Gauge size={17} color="#4f46e5" /> ICP por área
          </h4>
          <div style={{ display: 'grid', gap: 8 }}>
            {dados.icpPorArea.map((a) => {
              const c = corIcp(a.icp);
              return (
                <div key={a.area} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ minWidth: 190, fontSize: 13, fontWeight: 600, color: '#334155' }}>{a.area}</span>
                  <div style={{ flex: 1, height: 8, background: '#f1f5f9', borderRadius: 999, overflow: 'hidden' }}>
                    <div style={{ width: `${a.icp}%`, height: '100%', background: c.color, borderRadius: 999 }} />
                  </div>
                  <span style={{
                    minWidth: 62, textAlign: 'right', fontSize: 13, fontWeight: 800,
                    color: c.color, fontVariantNumeric: 'tabular-nums',
                  }}>
                    {a.icp.toFixed(1)}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* --- não conformidades por especialidade --- */}
      {dados.naoConformidades.porEspecialidade.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 18, marginBottom: 20 }}>
          <h4 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 800, color: '#0f172a' }}>
            Não conformidades por especialidade
          </h4>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {dados.naoConformidades.porEspecialidade.map((e) => (
              <span key={e.nome} style={{
                padding: '6px 12px', background: '#eef2ff', color: '#4338ca',
                borderRadius: 999, fontSize: 13, fontWeight: 700,
              }}>
                {e.nome}: <span style={{ fontVariantNumeric: 'tabular-nums' }}>{e.qtd}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* --- checklist completo --- */}
      {Object.entries(porArea).map(([area, itens]) => (
        <div key={area} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, marginBottom: 12, overflow: 'hidden' }}>
          <div style={{ padding: '12px 18px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontWeight: 800, color: '#0f172a' }}>
            {area}
          </div>
          <div style={{ padding: 14 }}>
            {itens.map((i) => {
              // Não conformidade (🟡/🔴): é ela que recebe baixa. A baixa só é
              // editável com o programa aberto (Inspecionado/Em execução).
              const ehAchadoItem = i.STATUS === 'Desgaste' || i.STATUS === 'Necessita Intervenção';
              const podeBaixar = podeGerenciar && ['Inspecionado', 'Em execução'].includes(p.STATUS);
              return (
                <div key={i.ID} style={{ padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    {iconeStatusItem(i.STATUS)}
                    <span style={{ fontWeight: 600, color: '#0f172a' }}>{i.ITEM_SNAPSHOT}</span>
                    {/* Marca visual do achado fora da lista — é o que explica por
                        que ele não aparece na conta do ICP. */}
                    {!i.ID_ITEM_TEMPLATE && (
                      <span style={{
                        padding: '1px 7px', background: '#fee2e2', color: '#991b1b',
                        borderRadius: 999, fontSize: 10, fontWeight: 800,
                      }}>
                        FORA DO CHECKLIST · não conta no ICP
                      </span>
                    )}
                    <span style={{ fontSize: 12, color: '#64748b', marginLeft: 'auto' }}>{i.STATUS}</span>
                  </div>
                  {i.OBSERVACAO && (
                    <div style={{ marginTop: 4, marginLeft: 23, fontSize: 13, color: '#475569' }}>{i.OBSERVACAO}</div>
                  )}
                  {/* Só a especialidade: a prioridade saiu do módulo em 2026-08-18. */}
                  {i.ESPECIALIDADE && (
                    <div style={{ marginTop: 4, marginLeft: 23, fontSize: 12, color: '#94a3b8' }}>
                      {i.ESPECIALIDADE}
                    </div>
                  )}
                  {i.FOTOS?.length > 0 && (
                    <div style={{ marginTop: 6, marginLeft: 23, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {/* Proxy: a webUrl do SharePoint é autenticada e não carrega direto num <img>. */}
                      {i.FOTOS.map((url) => (
                        <ImagemSharePoint key={url} url={url} alt="Foto do item" size={56} />
                      ))}
                    </div>
                  )}
                  {/* Baixa do "depois" no próprio item (DEM-00025, Fase B). */}
                  {ehAchadoItem && (
                    <BaixaAchado idPrograma={id} item={i} loja={p.LOJA} podeEditar={podeBaixar}
                      onMudou={carregar} onErro={setErro} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------
// Baixa do "depois" de uma não conformidade, NO PRÓPRIO ITEM (DEM-00025, Fase B).
// Foto(s) do depois + observação → grava BAIXA_EM. É o que o gate da
// certificação passou a exigir de todo achado. Sem foto = desfazer a baixa.
// ---------------------------------------------------------------------
const BaixaAchado = ({ idPrograma, item, loja, podeEditar, onMudou, onErro }) => {
  const [fotos, setFotos] = useState(item.FOTOS_DEPOIS || []);
  const [obs, setObs] = useState(item.OBSERVACAO_DEPOIS || '');
  const [salvando, setSalvando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const baixado = !!item.BAIXA_EM;

  const enviarFotos = async (arquivos) => {
    const lista = Array.from(arquivos || []).slice(0, 5 - fotos.length);
    if (!lista.length) return;
    setEnviando(true);
    const novas = [];
    try {
      for (const file of lista) {
        const fd = new FormData();
        fd.append('foto', file);
        fd.append('loja', loja || '');
        fd.append('area', item.AREA_SNAPSHOT || 'depois');
        const r = await fetch(`${API}/upload-foto`, { method: 'POST', headers: auth(), body: fd });
        const d = await r.json();
        if (!r.ok) throw new Error(d?.message || 'Falha no upload.');
        novas.push(d.url);
      }
    } catch (e) { onErro(e.message); } finally {
      if (novas.length) setFotos((f) => [...f, ...novas]);
      setEnviando(false);
    }
  };

  const salvar = async (desfazer) => {
    setSalvando(true);
    try {
      const r = await fetch(`${API}/${idPrograma}/achado/${item.ID}/baixa`, {
        method: 'PUT', headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ fotos_depois: desfazer ? [] : fotos, observacao_depois: obs || null }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.message || 'Erro ao salvar a baixa.');
      onMudou();
    } catch (e) { onErro(e.message); } finally { setSalvando(false); }
  };

  // Programa fechado (não pode editar) e sem baixa: nada a mostrar aqui.
  if (!podeEditar && !baixado) return null;

  return (
    <div style={{
      marginTop: 8, marginLeft: 23, padding: 12, borderRadius: 8,
      background: baixado ? '#f0fdf4' : '#fffbeb',
      border: `1px solid ${baixado ? '#bbf7d0' : '#fde68a'}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        {baixado ? <CheckCircle2 size={14} color="#059669" /> : <AlertTriangle size={14} color="#b45309" />}
        <strong style={{ fontSize: 12, color: baixado ? '#065f46' : '#92400e' }}>
          {baixado ? 'Baixa registrada (depois)' : 'Sem baixa — registre a foto do depois'}
        </strong>
        {baixado && item.BAIXA_POR && (
          <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 'auto' }}>
            por {item.BAIXA_POR} em {dataBr(item.BAIXA_EM)}
          </span>
        )}
      </div>

      {/* Fotos do depois */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: podeEditar ? 8 : 0 }}>
        {fotos.map((u) => (
          <span key={u} style={{ position: 'relative', display: 'inline-block' }}>
            <ImagemSharePoint url={u} alt="Depois" size={56} />
            {podeEditar && (
              <button type="button" onClick={() => setFotos(fotos.filter((x) => x !== u))}
                style={{
                  position: 'absolute', top: -6, right: -6, width: 18, height: 18, padding: 0,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%',
                  border: '1px solid #fecaca', background: '#fef2f2', color: '#b91c1c', cursor: 'pointer', lineHeight: 0, fontSize: 12,
                }}>×</button>
            )}
          </span>
        ))}
        {podeEditar && fotos.length < 5 && (
          <label style={{
            display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer',
            padding: '8px 12px', borderRadius: 6, fontSize: 12, fontWeight: 700,
            border: '1px dashed #a7f3d0', background: '#f0fdf4', color: '#059669',
          }}>
            {enviando ? <Loader2 size={14} className="spin" /> : <Plus size={14} />}
            {enviando ? 'Enviando…' : 'Foto do depois'}
            <input type="file" accept="image/*" multiple style={{ display: 'none' }} disabled={enviando}
              onChange={(e) => { enviarFotos(e.target.files); e.target.value = ''; }} />
          </label>
        )}
      </div>

      {podeEditar ? (
        <>
          <textarea value={obs} onChange={(e) => setObs(e.target.value)} rows={2}
            placeholder="O que foi feito (opcional)"
            style={{ width: '100%', padding: 8, border: '1px solid #cbd5e1', borderRadius: 6, boxSizing: 'border-box', fontSize: 13, resize: 'vertical' }} />
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <button onClick={() => salvar(false)} disabled={salvando || fotos.length === 0}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px',
                background: fotos.length === 0 ? '#e2e8f0' : '#059669', color: fotos.length === 0 ? '#94a3b8' : '#fff',
                border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: fotos.length === 0 ? 'not-allowed' : 'pointer',
              }}>
              {salvando ? <Loader2 size={14} className="spin" /> : <CheckCircle2 size={14} />} Dar baixa
            </button>
            {baixado && (
              <button onClick={() => salvar(true)} disabled={salvando}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                <Undo2 size={14} /> Desfazer baixa
              </button>
            )}
            {fotos.length === 0 && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#b45309', fontWeight: 600 }}>
                <AlertTriangle size={13} /> Anexe a foto do depois para dar baixa.
              </span>
            )}
          </div>
        </>
      ) : (
        obs && <div style={{ fontSize: 12.5, color: '#475569', marginTop: 4 }}><strong>Serviço:</strong> {obs}</div>
      )}
    </div>
  );
};

const Card = ({ titulo, valor, rodape }) => (
  <div style={{ padding: 16, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12 }}>
    <div style={{ fontSize: 11, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase' }}>{titulo}</div>
    <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>{valor}</div>
    {rodape && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{rodape}</div>}
  </div>
);

// ---------------------------------------------------------------------
// Painel de validação em duas assinaturas (gerente + consultor).
// O programa vira "Certificado" quando os dois assinam. O botão de cada papel
// só aparece para quem tem o perfil; o servidor é quem decide de fato.
// ---------------------------------------------------------------------
const PainelValidacao = ({ id, p, validacao, onValidou, onErro }) => {
  const [obs, setObs] = useState({ gerente: '', consultor: '' });
  const [salvando, setSalvando] = useState('');

  const certificado = p.STATUS === 'Certificado';
  const bloqueio = validacao.bloqueio;

  const assinar = async (papel) => {
    setSalvando(papel);
    try {
      const r = await fetch(`${API}/${id}/validar/${papel}`, {
        method: 'POST', headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ observacao: obs[papel] || undefined }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.message || 'Erro ao validar.');
      onValidou();
    } catch (e) { onErro(e.message); } finally { setSalvando(''); }
  };

  const bloco = (papel, rotulo, perfilPode) => {
    const v = validacao[papel];
    const assinado = !!v.nome;
    return (
      <div style={{ flex: 1, minWidth: 240, padding: 14, background: assinado ? '#f0fdf4' : '#fff', border: `1px solid ${assinado ? '#a7f3d0' : '#e2e8f0'}`, borderRadius: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
          {assinado ? <CheckCircle2 size={16} color="#059669" /> : <ShieldCheck size={16} color="#94a3b8" />}
          <strong style={{ fontSize: 13, color: '#0f172a' }}>{rotulo}</strong>
        </div>
        {assinado ? (
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#065f46' }}>{v.nome}</div>
            <div style={{ fontSize: 12, color: '#64748b' }}>{dataBr(v.data)}</div>
            {v.obs && <div style={{ marginTop: 6, fontSize: 12, color: '#475569', fontStyle: 'italic' }}>"{v.obs}"</div>}
            <div style={{ marginTop: 6, display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#94a3b8' }}>
              <Lock size={11} /> assinatura registrada
            </div>
          </div>
        ) : perfilPode ? (
          <div>
            <textarea value={obs[papel]} onChange={(e) => setObs({ ...obs, [papel]: e.target.value })}
              placeholder="Observação (opcional) — trava ao assinar" rows={2}
              disabled={!!bloqueio}
              style={{ width: '100%', padding: 8, border: '1px solid #cbd5e1', borderRadius: 6, boxSizing: 'border-box', fontSize: 13, resize: 'vertical' }} />
            <button onClick={() => assinar(papel)} disabled={!!bloqueio || salvando === papel}
              style={{
                marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
                background: bloqueio ? '#e2e8f0' : '#059669', color: bloqueio ? '#94a3b8' : '#fff',
                border: 'none', borderRadius: 8, fontWeight: 700, cursor: bloqueio ? 'not-allowed' : 'pointer',
              }}>
              {salvando === papel ? <Loader2 size={14} className="spin" /> : <ShieldCheck size={14} />} Validar como {rotulo}
            </button>
          </div>
        ) : (
          <div style={{ fontSize: 12, color: '#94a3b8' }}>Aguardando validação.</div>
        )}
      </div>
    );
  };

  return (
    <div style={{
      background: certificado ? '#ecfdf5' : '#fff',
      border: `1px solid ${certificado ? '#a7f3d0' : '#e2e8f0'}`,
      borderRadius: 12, padding: 18, marginBottom: 20,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <ShieldCheck size={18} color={certificado ? '#059669' : '#4f46e5'} />
        <h4 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#0f172a' }}>
          {certificado ? 'Certificação concluída' : 'Validação da conservação'}
        </h4>
        {certificado && (
          <span style={{ padding: '2px 10px', background: '#d1fae5', color: '#065f46', borderRadius: 999, fontSize: 12, fontWeight: 800 }}>
            CERTIFICADO
          </span>
        )}
      </div>

      {/* Gate: OS em aberto impede a certificação. */}
      {bloqueio && !certificado && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 12, marginBottom: 12, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, color: '#92400e', fontSize: 13 }}>
          <AlertTriangle size={16} /> {bloqueio}
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {bloco('gerente', 'Gerente da Unidade', podeGerente)}
        {bloco('consultor', 'Consultor de Manutenção', podeConsultor)}
      </div>
    </div>
  );
};

const botaoVoltar = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
  background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, fontWeight: 700, cursor: 'pointer',
};

export default ConservacaoHistorico;
